import type { ComponentType, LazyExoticComponent } from "react";

export type ToastKind = "success" | "error" | "info";

export type ToolComponentProps = {
  onToast: (message: string, kind?: ToastKind) => void;
};

export type ToolLoader = () => Promise<{
  default: ComponentType<ToolComponentProps> | LazyExoticComponent<ComponentType<ToolComponentProps>>;
}>;

export type ToolDefinition = {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  load: ToolLoader;
};

export const toolRegistry: ToolDefinition[] = [
  {
    id: "markdown",
    label: "Markdown",
    description: "Convert Markdown to clean text, HTML, DOCX, and print-ready PDF locally.",
    enabled: true,
    load: () => import("../tools/markdown/MarkdownTool").then((module) => ({ default: module.MarkdownTool }))
  },
  {
    id: "text",
    label: "Text",
    description: "Clean copied text and remove formatting without saving your content.",
    enabled: true,
    load: () => import("../tools/text/TextCleanerTool").then((module) => ({ default: module.TextCleanerTool }))
  },
  {
    id: "pdf",
    label: "PDF",
    description: "Merge, split, extract, delete, reorder, and unlock known-password PDFs locally.",
    enabled: true,
    load: () => import("../tools/pdf/PdfTools").then((module) => ({ default: module.PdfTools }))
  },
  {
    id: "json",
    label: "JSON / CSV",
    description: "Convert between JSON and CSV, or pretty-print JSON — all locally.",
    enabled: true,
    load: () => import("../tools/json/JsonTool").then((module) => ({ default: module.JsonTool }))
  },
  {
    id: "image",
    label: "Image",
    description: "Convert and resize images between PNG, JPEG, and WebP locally.",
    enabled: true,
    load: () => import("../tools/image/ImageTool").then((module) => ({ default: module.ImageTool }))
  },
  {
    id: "base64",
    label: "Base64",
    description: "Encode or decode text and files as Base64 — entirely in your browser.",
    enabled: true,
    load: () => import("../tools/base64/Base64Tool").then((module) => ({ default: module.Base64Tool }))
  },
  {
    id: "slack",
    label: "→ Slack",
    description: "Convert Markdown to Slack mrkdwn so headings, bold, italics, and links render correctly.",
    enabled: true,
    load: () => import("../tools/slack/SlackTool").then((module) => ({ default: module.SlackTool }))
  },
  {
    id: "privacy",
    label: "Privacy",
    description: "See exactly why no backend, uploads, API calls, analytics, or tracking are used.",
    enabled: false,
    load: () => import("../tools/privacy/PrivacyTool").then((module) => ({ default: module.PrivacyTool }))
  }
];
